import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Activity, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { teilnahmeAPI } from '@/lib/api';
import { SEGMENT, TREND, datumKurz, komma } from '@/components/teilnahme/teilnahmeText';

/* Teilnahmehäufigkeit (v9 A, 23.10.2026): wer trainiert wie oft, wer springt ab. Besuch = Tag mit Check-in oder gebuchtem
   Kurs (wie der Kursbestand), Octiv-Altdaten nur als Summe. „Abwesend“ nutzt die Schwelle der Reaktivierung. */
const FILTER = [['kurs', 'Kurs'], ['sparte', 'Sparte'], ['tarif', 'Tarif'], ['alter', 'Alter'], ['trainer', 'Trainer']];

export default function TeilnahmePage() {
  const [f, setF] = useState({ kurs: '', sparte: '', tarif: '', alter: '', trainer: '', segment: '' });
  const [d, setD] = useState(null);
  const [laedt, setLaedt] = useState(true);
  const [csvBusy, setCsvBusy] = useState(false);
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  const lade = useCallback(() => {
    setLaedt(true);
    teilnahmeAPI.liste(params).then((r) => setD(r.data)).catch((e) => toast.error(e?.response?.data?.detail || 'Nicht geladen')).finally(() => setLaedt(false));
  }, [JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { lade(); }, [lade]);
  const setze = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const exportieren = async () => {
    setCsvBusy(true);
    try {
      const r = await teilnahmeAPI.csv(params);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `teilnahme_${d?.stand || ''}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export fehlgeschlagen'); }
    setCsvBusy(false);
  };
  const opt = d?.optionen || {};
  const auswahl = { kurs: (opt.kurse || []).map((k) => [k.id, k.name]), sparte: (opt.sparten || []).map((s) => [s, s]),
    tarif: (opt.tarife || []).map((t) => [t, t]), alter: [['kids', 'Kids/Jugend'], ['erwachsene', 'Erwachsene']], trainer: (opt.trainer || []).map((t) => [t, t]) };

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="teilnahme-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2"><Activity className="w-6 h-6 text-marke" /> Teilnahme</h1>
          <p className="text-sm text-muted-foreground">Wer trainiert wie oft? Besuche aus Check-ins und gebuchten Kursen, letzte 12 Wochen.</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportieren} disabled={csvBusy || !d} className="gap-1.5" data-testid="teilnahme-csv">
          {csvBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} CSV
        </Button>
      </div>

      {d?.kennzahl && (
        <p className="text-sm"><b>{komma(d.kennzahl.anteil)} %</b> der Mitglieder trainieren mindestens einmal je Woche
          <span className="text-muted-foreground"> ({d.kennzahl.anzahl} von {d.kennzahl.basis}, Ø 4 Wochen, ohne pausierte)</span></p>
      )}

      <div className="flex flex-wrap gap-2" data-testid="teilnahme-segmente">
        <button type="button" onClick={() => setF((x) => ({ ...x, segment: '' }))}
          className={`rounded-full border px-3 py-1 text-xs font-semibold ${!f.segment ? 'border-marke text-marke' : 'border-border'}`}>Alle</button>
        {Object.entries(SEGMENT).map(([k, s]) => (
          <button key={k} type="button" onClick={() => setF((x) => ({ ...x, segment: x.segment === k ? '' : k }))} title={s.erklaerung}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${s.klasse} ${f.segment === k ? 'ring-2 ring-marke' : ''}`}>
            {s.text} {d?.segmente?.[k] ?? 0}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTER.map(([k, t]) => (
          <select key={k} value={f[k]} onChange={setze(k)} className="h-8 rounded-md border border-border bg-background px-2 text-xs" aria-label={t} data-testid={`teilnahme-filter-${k}`}>
            <option value="">{t}: alle</option>
            {auswahl[k].map(([w, l]) => <option key={w} value={w}>{l}</option>)}
          </select>
        ))}
      </div>

      {f.segment === 'abwesend' && (
        <p className="text-xs text-muted-foreground">Abwesend heißt: kein Besuch seit der Schwelle der Reaktivierung (Kinder/Jugend und Erwachsene getrennt).
          Anrufen und nachfassen läuft wie bisher über die <Link to="/reaktivierung" className="text-marke hover:underline">Reaktivierung</Link>.</p>
      )}

      {laedt && !d ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
        <div className="overflow-x-auto rounded-xl border border-border/60">
          <table className="w-full text-sm">
            <thead><tr className="bg-muted/40 text-left text-xs text-muted-foreground">
              {['Mitglied', 'Segment', '4 Wo.', '12 Wo.', 'Ø/Wo.', 'Trend', 'Letzter Besuch', 'Kurse'].map((h) => <th key={h} className="px-3 py-2 font-semibold whitespace-nowrap">{h}</th>)}
            </tr></thead>
            <tbody>
              {(d?.zeilen || []).map((z) => {
                const s = SEGMENT[z.segment] || SEGMENT.selten;
                const tr = TREND[z.trend] || TREND.gleich;
                return (
                  <tr key={z.lead_id} className="border-t border-border/60">
                    <td className="px-3 py-2"><Link to={`/leads/${z.lead_id}`} className="font-medium hover:underline">{z.name || 'Ohne Name'}</Link>
                      <div className="text-[11px] text-muted-foreground">{z.tarif}{z.altersgruppe === 'kids' ? ' · Kids' : ''}</div></td>
                    <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${s.klasse}`}>{s.text}</span></td>
                    <td className="px-3 py-2 tabular-nums">{z.besuche_4w}</td>
                    <td className="px-3 py-2 tabular-nums">{z.besuche_12w}</td>
                    <td className="px-3 py-2 tabular-nums">{komma(z.schnitt_woche)}</td>
                    <td className={`px-3 py-2 font-semibold ${tr.klasse}`} title={tr.text}>{tr.zeichen}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{z.letzter_besuch ? `${datumKurz(z.letzter_besuch)} (${z.tage_seit} T.)` : '–'}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{z.kurse.join(', ') || '–'}</td>
                  </tr>
                );
              })}
              {d && d.zeilen.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">Niemand in dieser Auswahl.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
